import { llamaLendChainData, llamaLendConfigFor } from '@1delta/data-sdk'
import { encodeAbiParameters, parseAbiParameters } from 'viem'
import { Call } from '../../../utils/multicall'
import {
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'

const ZERO = '0x0000000000000000000000000000000000000000'

/** Words per lens row — must match the layout documented on `PositionLens.llamaLend`. */
export const LLAMALEND_LENS_WORDS = 12

/**
 * User-data call build for Curve LlamaLend. Positions are keyed by OWNER
 * ADDRESS — the Controller enforces at most ONE loan per address per market
 * (`assert loan[_for].initial_debt == 0`) — so there is no discovery phase and
 * the layout is fully deterministic from the market roster.
 *
 * per market (roster order):
 *   Controller.user_state(account)   — `[collateral, borrowed, debt, N]`
 *   Controller.health(account, true) — signed; < 0 ⇒ hard-liquidatable
 *   Controller.user_prices(account)  — `[price_upper, price_lower]`, the
 *                                      SOFT-LIQUIDATION BAND RANGE
 *   AMM.read_user_tick_numbers(acc)  — `[n1, n2]`, the occupied bands
 *   Controller.approval(account, spender) — is our spender delegated here
 *   Vault.balanceOf(account)         — UNSTAKED lend shares
 *   Gauge.balanceOf(account)         — STAKED lend shares
 *   Vault.convertToAssets(1e18)      — share price, same block as the balances
 *
 * The `approval` read is what gates every on-behalf route, and it is per
 * market rather than per protocol: older-blueprint controllers have no such
 * method at all, so the call fails and parses as `false`. That is the correct
 * answer for them — fail closed.
 *
 * THE SUPPLY SIDE NEEDS BOTH SHARE READS. A LlamaLend market's lend side is an
 * ERC-4626 vault, and this call set used to touch only the Controller and the
 * AMM — i.e. only the BORROW side — so a pure lender read as having no position
 * at all. Worse, the vault balance alone is not enough: every market has a live
 * Curve gauge and staking is the norm rather than the exception. Measured on
 * Ethereum, the share of supply sitting in the gauge is 99.7 % (WBTC), 99.4 %
 * (wstETH), 99.4 % (CRV), 95.9 % (sUSDe) and 91.2 % (WETH) on the markets that
 * use it — so reading `Vault.balanceOf` alone would miss almost every lender on
 * almost every market. Shares are 1:1 between vault and gauge, so the position
 * is simply their sum.
 *
 * Neither balance is an AMOUNT. `DEAD_SHARES` puts shares ~1000x the asset
 * scale, so the price per share is read HERE rather than taken from the cached
 * public row: a fresh balance multiplied by a stale price is a wrong number,
 * and reading both in one multicall makes that impossible by construction.
 *
 * `user_state` returning BOTH a collateral and a `borrowed` leg is not an
 * error: during soft liquidation the LLAMMA converts part of the user's
 * collateral into the borrowed token inside their own bands. That balance
 * offsets debt and has to be reported, not discarded.
 */
export const LLAMALEND_CALLS_PER_MARKET = 8

/**
 * True when this chain's reads go through `PositionLens` instead of the roster walk
 * above. Derived from the address map so the builder and the converter can never
 * disagree about the layout — they call this, they do not each decide.
 */
export const usesLlamaLendLens = (chainId: string): boolean =>
  getPositionLensFor(chainId, LENS_FAMILY.LLAMALEND) !== undefined

/**
 * ONE call instead of 8 per market — 232 calls on Ethereum's 29 markets, for an
 * account that is in none of them in the overwhelming majority of cases.
 *
 * The lens probes `loan_exists` (a single storage slot) plus the two share
 * balances, and only pays for `health` / `user_prices` / `read_user_tick_numbers`
 * — the LLAMMA band walk, which is what makes this read heavy — on markets that
 * answer. The skip condition is equivalent to the roster walk's, not merely
 * similar: collateral and the soft-liquidated leg exist only inside a loan, so
 * `loan_exists == false` implies both are zero.
 */
const buildLlamaLendLensCall = (
  chainId: string,
  markets: { controller: string; amm: string; vault: string; gauge?: string }[],
  account: string,
  delegate: string,
): Call[] => {
  const lens = getPositionLensFor(chainId, LENS_FAMILY.LLAMALEND)
  if (!lens) return []
  const payload = encodeAbiParameters(
    parseAbiParameters('(address,address,address,address)[], address'),
    [
      markets.map((m) => [
        m.controller,
        m.amm,
        m.vault,
        m.gauge ?? ZERO,
      ]) as any,
      delegate as `0x${string}`,
    ],
  )
  return [
    {
      address: lens,
      name: 'getUserData',
      params: [[{ family: LENS_FAMILY.LLAMALEND, payload }], account],
      abi: PositionLensAbi as any,
    },
  ]
}

export const buildLlamaLendUserCall = (
  chainId: string,
  lender: string,
  account: string,
  /**
   * The address whose delegation status to probe. Defaults to the account
   * itself, which trivially reads `false` and keeps the call layout fixed.
   *
   * NOTE this is NOT the leverage zap: leverage is EOA-direct (the user calls
   * the Controller, so `msg.sender` is already the borrower) and needs no
   * grant. Delegation only matters for a contract acting on the user's behalf
   * in the plain borrow/repay flows.
   */
  spender?: string,
): Call[] => {
  const cfg = llamaLendConfigFor(lender, chainId)
  const markets = llamaLendChainData(lender, chainId)?.markets ?? []
  if (!cfg || markets.length === 0) return []

  const delegate = spender ?? account

  // A chain without a lens deployment keeps the roster walk. That is the rollout
  // switch and the emergency revert both — removing the chain from the address map
  // restores this path with no redeploy and no other code change.
  if (usesLlamaLendLens(chainId)) {
    return buildLlamaLendLensCall(chainId, markets as any, account, delegate)
  }

  return markets.flatMap((m) => [
    { address: m.controller, name: 'user_state', params: [account] },
    { address: m.controller, name: 'health', params: [account, true] },
    { address: m.controller, name: 'user_prices', params: [account] },
    { address: m.amm, name: 'read_user_tick_numbers', params: [account] },
    { address: m.controller, name: 'approval', params: [account, delegate] },
    { address: m.vault, name: 'balanceOf', params: [account] },
    // A market without a gauge would call address(0), which returns '0x' and
    // parses as zero — the layout stays fixed either way.
    {
      address: m.gauge ?? '0x0000000000000000000000000000000000000000',
      name: 'balanceOf',
      params: [account],
    },
    { address: m.vault, name: 'convertToAssets', params: [10n ** 18n] },
  ])
}
